import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconArrowLeft,
  IconEdit,
  IconRoad,
  IconTrash,
  IconCheck,
} from "@tabler/icons-react";

import { api, ApiError } from "../lib/api";
import type { Block } from "../lib/types";
import { BlockForm } from "../components/BlockForm";
import { Modal } from "../components/Modal";

export function BlockDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);

  const blockQ = useQuery({
    queryKey: ["block", id],
    queryFn: () => api.get<Block>(`/v1/blocks/${id}`),
    enabled: !!id,
  });

  const deleteMut = useMutation({
    mutationFn: () => api.del(`/v1/blocks/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["blocks"] });
      navigate("/estate-land");
    },
  });

  if (blockQ.isLoading) {
    return <div className="text-xs text-text-secondary p-4">Loading block…</div>;
  }
  if (blockQ.error) {
    const msg = blockQ.error instanceof ApiError ? blockQ.error.message : "Failed to load";
    return (
      <div className="bg-bg-danger text-text-danger border border-border-danger rounded p-4 text-xs">
        {msg}
      </div>
    );
  }
  const b = blockQ.data?.data;
  if (!b) return null;

  const routerSuggestion = suggestRoute(b);

  return (
    <div className="text-sm">
      <button
        onClick={() => navigate("/estate-land")}
        className="text-xs text-text-accent flex items-center gap-1 mb-2"
      >
        <IconArrowLeft size={13} /> Back to blocks
      </button>

      {/* Header */}
      <div className="bg-surface-2 border border-border rounded-xl overflow-hidden mb-4">
        <div className="flex items-center justify-between p-3.5 bg-surface-1 border-b border-border">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <span className="font-mono text-xl font-medium">Block {b.code}</span>
              <CategoryBadge cat={b.category} />
              {b.innerRoadToMill && (
                <span className="bg-bg-warning text-text-warning px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1">
                  <IconRoad size={12} /> Inner road · {b.millRoadKm ?? "?"} km
                </span>
              )}
            </div>
            <div className="text-xs text-text-secondary">
              {b.plantingMaterialType && (
                <span>
                  {b.plantingMaterialType}
                  {b.plantingMaterialDetails ? ` · ${b.plantingMaterialDetails}` : ""}
                  {" · "}
                </span>
              )}
              {b.yearOfPlanting && <span>YOP {b.yearOfPlanting} · </span>}
              {b.topography && <span>{b.topography}</span>}
            </div>
          </div>
          <div className="flex gap-1.5">
            <button className="btn-ghost h-8" onClick={() => setEditOpen(true)}>
              <IconEdit size={13} /> Edit
            </button>
            <button
              className="btn-ghost h-8 hover:!bg-bg-danger hover:!text-text-danger"
              onClick={() => {
                if (confirm(`Soft-delete block ${b.code}?`)) deleteMut.mutate();
              }}
            >
              <IconTrash size={13} />
            </button>
          </div>
        </div>

        {/* Stat tiles */}
        <div className="p-3 grid grid-cols-4 gap-2">
          <Stat label="Hectares total" value={b.hectaresTotal ?? "—"} />
          <Stat label="Planted" value={b.hectaresPlanted ?? "—"} />
          <Stat label="YOP" value={b.yearOfPlanting ?? "—"} />
          <Stat label="Land type" value={b.landType ?? "—"} />
        </div>
      </div>

      {/* Delivery flags panel */}
      <div className="bg-surface-2 border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-surface-1 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-sm font-medium flex items-center gap-1.5">
              <IconRoad size={16} className="text-text-warning" /> Delivery flags
            </div>
            <div className="text-[11px] text-text-secondary mt-0.5">
              Drive Vehicle Planning's route suggestion. Admin-only.
            </div>
          </div>
        </div>
        <div className="p-4">
          <FlagRow label="inner_road_to_mill" value={b.innerRoadToMill ? "true" : "false"} />
          <FlagRow label="mill_road_km" value={b.millRoadKm ?? "—"} />
          <FlagRow label="dist_to_nearest_ramp_km" value={b.distToNearestRampKm ?? "—"} />
          <FlagRow label="default_ramp_id" value={b.defaultRampId ?? "—"} />
          <FlagRow label="route_override" value={b.routeOverride} />
        </div>

        {/* Router preview */}
        <div className="border-t border-border p-4">
          <div className="text-[11px] text-text-secondary uppercase tracking-wide mb-2">
            <IconCheck size={13} className="inline -mt-0.5 mr-1" />
            Router would suggest
          </div>
          <div
            className={`px-3 py-2 rounded text-xs font-medium ${
              routerSuggestion.className
            }`}
          >
            {routerSuggestion.label}
          </div>
          <div className="text-[11px] text-text-secondary mt-1.5 leading-relaxed">
            {routerSuggestion.reason}
          </div>
        </div>
      </div>

      {/* Edit modal */}
      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={`Edit block ${b.code}`}
        subtitle="Delivery flags update the Router immediately after save"
      >
        <BlockForm
          initial={b}
          onSaved={() => {
            setEditOpen(false);
          }}
          onCancel={() => setEditOpen(false)}
        />
      </Modal>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-surface-1 rounded p-2.5">
      <div className="text-[11px] text-text-secondary">{label}</div>
      <div className="text-base font-medium">{value}</div>
    </div>
  );
}

function FlagRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[200px_1fr] gap-3 py-2 border-b border-border last:border-b-0 text-xs">
      <div className="font-mono text-text-muted">{label}</div>
      <div className="font-mono">{value}</div>
    </div>
  );
}

function CategoryBadge({ cat }: { cat: Block["category"] }) {
  const cls =
    cat === "PLANTED"
      ? "bg-bg-success text-text-success"
      : cat === "LAND_CLEARING"
        ? "bg-bg-warning text-text-warning"
        : "bg-surface-1 text-text-secondary border border-border";
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {cat === "LAND_CLEARING" ? "Land clearing" : cat === "PLANTED" ? "Planted" : "Unplanted"}
    </span>
  );
}

function suggestRoute(b: Block) {
  const kmToMill = b.millRoadKm ? Number(b.millRoadKm) : null;
  const kmToRamp = b.distToNearestRampKm ? Number(b.distToNearestRampKm) : null;

  if (b.routeOverride === "FORCE_RAMP") {
    return { label: "Ramp-routed (forced)", className: "bg-[rgba(127,119,221,0.15)] text-[rgb(83,74,183)]", reason: "route_override = FORCE_RAMP overrides all flag-based logic." };
  }
  if (b.routeOverride === "FORCE_DIRECT_TRUCK") {
    return { label: "Direct · Truck (forced)", className: "bg-bg-success text-text-success", reason: "route_override = FORCE_DIRECT_TRUCK overrides all flag-based logic." };
  }
  if (b.routeOverride === "NEVER_GRABBER") {
    return { label: "Ramp-routed (no-grabber policy)", className: "bg-[rgba(127,119,221,0.15)] text-[rgb(83,74,183)]", reason: "route_override = NEVER_GRABBER excludes grabber; falls back to Ramp-routed." };
  }

  if (b.innerRoadToMill && kmToMill !== null && kmToMill <= 5) {
    return {
      label: "Direct · Grabber",
      className: "bg-bg-warning text-text-warning",
      reason: `inner_road=true AND mill_road_km (${kmToMill}) ≤ 5 → grabber viable for small volumes. Volume constraint (≤ grabber capacity ~6 t) still applies per-dispatch.`,
    };
  }
  if (kmToMill !== null && kmToRamp !== null && kmToMill < kmToRamp) {
    return {
      label: "Direct · Truck",
      className: "bg-bg-success text-text-success",
      reason: `dist_to_mill (${kmToMill}) < dist_to_nearest_ramp (${kmToRamp}) → skip ramp, truck straight to mill.`,
    };
  }
  return {
    label: "Ramp-routed",
    className: "bg-[rgba(127,119,221,0.15)] text-[rgb(83,74,183)]",
    reason: "No eligible flags — default consolidation at the nearest ramp before truck to mill.",
  };
}
